import React, { useState, useEffect, useMemo } from 'react';
import {
    Box,
    useTheme,
    useMediaQuery,
    Typography,
    Dialog,
    DialogContent,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import CommonCard from '../../../../components/card/CommonCard';
import ChampionshipCard from '../../../../components/championship';
import { membershipAPI, championAPI, contentAPI } from '../../../../actions/imsServer';
import { API_PAGE_SIZE } from '../../../../constants';
import { ChampionshipBackground } from '../../../../assets/images/championshipBackground';
import ChampionshipDetailContent from '../../../../components/championship/ChampionshipDetailContent';
import Button from '../../../../components/button';
import { getLocalizedName } from '../../../../utils/base';

export const ChampionshipTab = ({ userData, selectedOrganization }) => {
    const theme = useTheme();
    const { t, i18n } = useTranslation();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    // 실제 데이터 상태
    const [championships, setChampionships] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // 팝업 상태
    const [detailDialogOpen, setDetailDialogOpen] = useState(false);
    const [selectedChampionshipId, setSelectedChampionshipId] = useState(null);
    const [selectedChampionshipData, setSelectedChampionshipData] = useState(null);

    // 진행중/완료 대회 분리
    const { ongoingChampionships, completedChampionships } = useMemo(() => {
        const ongoing = [];
        const completed = [];
        const processedIds = new Set(); // 이미 처리된 id 추적

        championships.forEach(championship => {
            // 중복 체크
            if (processedIds.has(championship.id)) {
                return;
            }
            processedIds.add(championship.id);

            // state 기준으로 진행중/완료 구분
            // state: 0 → 진행중, state: 2 → 완료
            const state = championship.state !== undefined ? championship.state : 0;
            if (state === 2) {
                completed.push(championship);
            } else {
                ongoing.push(championship);
            }
        });

        // 완료된 대회는 최근 1년 내 것만 표시
        const oneYearAgo = new Date();
        oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
        const oneYearAgoStr = oneYearAgo.toISOString().split('T')[0];

        const filteredCompleted = completed.filter(championship => {
            const endDate = championship.endDate ? String(championship.endDate).slice(0, 10) : '';
            return endDate >= oneYearAgoStr;
        });

        // 날짜순 정렬 (최신순)
        filteredCompleted.sort((a, b) => {
            const dateA = a.endDate ? String(a.endDate).slice(0, 10) : '';
            const dateB = b.endDate ? String(b.endDate).slice(0, 10) : '';
            return dateB.localeCompare(dateA);
        });

        return {
            ongoingChampionships: ongoing,
            completedChampionships: filteredCompleted
        };
    }, [championships]);

    // 실제 데이터 가져오기
    const fetchChampionships = async () => {
        if (!userData?.id) return;

        try {
            setLoading(true);
            setError(null);

            // 사용자의 기관 ID 목록 가져오기
            const membershipsResponse = await membershipAPI.getMemberships({
                userId: userData.id,
                type: 1,
                state: 0,
                expand: 'organization'
            });

            const userOrganizationIds = [];
            if (membershipsResponse?.data?.memberships) {
                membershipsResponse.data.memberships.forEach(membership => {
                    if (membership.organizationId) {
                        userOrganizationIds.push(String(membership.organizationId));
                    }
                });
            }

            // 선택된 기관이 있으면 해당 기관만, 없으면 가입한 모든 기관과 0 포함
            const championParams = { state: '0,2', expand: 'ranker' };
            if (selectedOrganization) {
                // 선택된 기관과 0(전체)만 조회
                championParams.organizationId = `${selectedOrganization},0`;
            } else if (userOrganizationIds.length > 0) {
                // 선택된 기관이 없으면 가입한 모든 기관과 0 포함
                championParams.organizationId = `${userOrganizationIds.join(',')},0`;
            } else {
                // 기관이 없으면 0만
                championParams.organizationId = '0';
            }

            const fetchChampionsBatched = async () => {
                let all = [];
                let offset = 0;
                let page;
                do {
                    const res = await championAPI.getChampions({ ...championParams, offset });
                    page = res?.data?.champions || [];
                    all = all.concat(page);
                    offset += API_PAGE_SIZE;
                } while (page.length === API_PAGE_SIZE);
                return all;
            };

            const fetchContentsBatched = async () => {
                let all = [];
                let offset = 0;
                let page;
                do {
                    const res = await contentAPI.getContents({ state: 0, offset });
                    page = res?.data?.contents || [];
                    all = all.concat(page);
                    offset += API_PAGE_SIZE;
                } while (page.length === API_PAGE_SIZE);
                return all;
            };

            const [champions, contents] = await Promise.all([
                fetchChampionsBatched(),
                fetchContentsBatched()
            ]);

            // 콘텐츠 이름 매핑
            const uniqueContentIndexes = [...new Set(
                champions
                    .map(c => c.contentIndex)
                    .filter(index => index !== null && index !== undefined)
            )];

            let contentNameMap = {};
            let contentIconMap = {};
            if (uniqueContentIndexes.length > 0) {
                try {
                    contentNameMap = (contents || []).reduce((acc, content) => {
                        if (content && content.index !== undefined) {
                            acc[String(content.index)] = getLocalizedName(content, i18n.language) || content.name || '';
                        }
                        return acc;
                    }, {});
                    contentIconMap = contents.reduce((acc, content) => {
                        if (content && content.index !== undefined && content.icon) {
                            acc[String(content.index)] = content.icon;
                        }
                        return acc;
                    }, {});
                } catch (e) {
                    console.warn('콘텐츠 정보 로딩 실패:', e);
                }
            }

            // 챔피언십 데이터를 카드 형식으로 변환
            const transformedChampionships = champions
                .filter(c => {
                    // 선택된 기관이 있으면 해당 기관의 챔피언십만 필터링
                    if (selectedOrganization) {
                        const orgId = String(c.organizationId || '');
                        const selectedOrgId = String(selectedOrganization);
                        return orgId === selectedOrgId || c.organizationId === 0;
                    }
                    return true;
                })
                .map(c => {
                    const start = c.startDate ? String(c.startDate).slice(0, 10) : '';
                    const end = c.endDate ? String(c.endDate).slice(0, 10) : '';

                    // 현재 우승자 정보 (ranker가 있으면)
                    let currentWinner = '';
                    if (c.ranker && c.ranker.length > 0) {
                        const topRanker = c.ranker[0];
                        currentWinner = topRanker.name || '';
                    }

                    return {
                        id: c.id,
                        type: c.type || 0, // 0: 랭킹전, 1: 토너먼트
                        state: c.state || 0,
                        contentName: contentNameMap[String(c.contentIndex)] || '',
                        icon: contentIconMap[String(c.contentIndex)] || '',
                        name: c.name || '',
                        locked: !!c.password,
                        startDate: start,
                        endDate: end,
                        currentWinner: currentWinner,
                        image: null, // 이미지는 콘텐츠에서 가져올 수 있지만 현재는 null
                        userId: c.userId,
                        organizationId: c.organizationId
                    };
                });

            // 중복 제거 (같은 id를 가진 항목 중 첫 번째만 유지)
            const uniqueChampionships = transformedChampionships.reduce((acc, current) => {
                const existingIndex = acc.findIndex(item => item.id === current.id);
                if (existingIndex === -1) {
                    acc.push(current);
                }
                return acc;
            }, []);

            // 본인이 만든 챔피언십만 필터링
            const userCreatedChampionships = uniqueChampionships.filter(championship => {
                return championship.userId === userData.id;
            });

            setChampionships(userCreatedChampionships);
        } catch (err) {
            console.error('챔피언십 데이터 로딩 실패:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchChampionships();
        // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchChampionships는 의존성에서 제외
    }, [userData?.id, selectedOrganization, i18n.language]);

    const handleCardClick = (championshipId) => {
        const championshipData = championships.find(card => card.id === championshipId);
        setSelectedChampionshipId(championshipId);
        setSelectedChampionshipData(championshipData);
        setDetailDialogOpen(true);
    };

    const handleCloseDetailDialog = () => {
        setDetailDialogOpen(false);
        setSelectedChampionshipId(null);
        setSelectedChampionshipData(null);
    };

    // 공통 컬럼 스타일 (화면 너비에 맞춰 균등 분할)
    const columnStyle = {
        display: 'flex',
        flexDirection: 'column',
        gap: 1.25,
        flex: 1,
        minWidth: 0
    };

    return (
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'row', gap: 2, px: 1.25, width: '100%' }}>
            {/* 왼쪽: 진행중인 대회 */}
            <Box sx={columnStyle}>
                {/* 제목 */}
                <Typography variant="h6" sx={{
                    fontWeight: 600,
                    color: theme.palette.figma.white[100],
                    mb: 1
                }}>
                    {t('event_status_ongoing')}
                </Typography>

                {/* 대회 카드들 */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                    {loading ? (
                        <CommonCard sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: theme.palette.figma.white[5]
                        }}>
                            <Typography variant="h6" sx={{
                                color: theme.palette.figma.white[60]
                            }}>
                                {t('loading')}
                            </Typography>
                        </CommonCard>
                    ) : error ? (
                        <CommonCard sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: theme.palette.figma.white[5]
                        }}>
                            <Typography variant="h6" sx={{
                                color: theme.palette.figma.calorieRed
                            }}>
                                {t('error')}: {error}
                            </Typography>
                        </CommonCard>
                    ) : ongoingChampionships.length > 0 ? (
                        ongoingChampionships.map((championship, index) => (
                            <ChampionshipCard
                                key={`ongoing-${championship.id}-${index}`}
                                {...championship}
                                onClick={() => handleCardClick(championship.id)}
                                sx={{ width: '100%' }}
                            />
                        ))
                    ) : (
                        <CommonCard sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: theme.palette.figma.white[5]
                        }}>
                            <Box sx={{ textAlign: 'center' }}>
                                <Typography variant="h6" sx={{
                                    color: theme.palette.figma.white[60],
                                }}>
                                    {t('no_ongoing_championship')}
                                </Typography>
                            </Box>
                        </CommonCard>
                    )}
                </Box>
            </Box>

            {/* 오른쪽: 완료된 대회 */}
            <Box sx={columnStyle}>
                {/* 제목 */}
                <Typography variant="h6" sx={{
                    fontWeight: 600,
                    color: theme.palette.figma.white[100],
                    mb: 1
                }}>
                    {t('complete')}
                </Typography>

                {/* 대회 카드들 */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                    {loading ? (
                        <CommonCard sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: theme.palette.figma.white[5]
                        }}>
                            <Typography variant="h6" sx={{
                                color: theme.palette.figma.white[60]
                            }}>
                                {t('loading')}
                            </Typography>
                        </CommonCard>
                    ) : error ? (
                        <CommonCard sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: theme.palette.figma.white[5]
                        }}>
                            <Typography variant="h6" sx={{
                                color: theme.palette.figma.calorieRed
                            }}>
                                {t('error')}: {error}
                            </Typography>
                        </CommonCard>
                    ) : completedChampionships.length > 0 ? (
                        <>
                            {completedChampionships.map((championship, index) => (
                                <ChampionshipCard
                                    key={`completed-${championship.id}-${index}`}
                                    {...championship}
                                    onClick={() => handleCardClick(championship.id)}
                                    sx={{ width: '100%' }}
                                />
                            ))}


                        </>
                    ) : (
                        <CommonCard sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: theme.palette.figma.white[5]
                        }}>
                            <Typography variant="h6" sx={{
                                color: theme.palette.figma.white[60]
                            }}>
                                {t('no_completed_championship')}
                            </Typography>
                        </CommonCard>
                    )}
                </Box>
            </Box>

            {/* 챔피언십 상세 팝업 */}
            <Dialog
                open={detailDialogOpen}
                onClose={handleCloseDetailDialog}
                slotProps={{
                    root: { keepMounted: false },
                }}
                PaperProps={{
                    sx: {
                        borderRadius: '20px',
                        background: theme.palette.figma.popupBG || theme.palette.figma.block,
                        width: '360px',
                        display: 'flex',
                        flexDirection: 'column',
                    },
                }}
            >
                <DialogContent
                    sx={{
                        padding: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                        position: 'relative',
                        minHeight: '600px',
                    }}
                >
                    {/* Background */}
                    <Box sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'stretch',
                        justifyContent: 'stretch',
                        zIndex: 0
                    }}>
                        <ChampionshipBackground sx={{ width: '100%', height: '100%' }} />
                    </Box>

                    {/* 챔피언십 상세 컴포넌트 */}
                    {selectedChampionshipId && selectedChampionshipData && (
                        <Box sx={{
                            width: '100%',
                            flex: 1,
                            overflow: 'auto',
                            display: 'flex',
                            flexDirection: 'column',
                            position: 'relative',
                            zIndex: 1,
                            // userDetail.js의 Navigation 영역 높이만큼 상단 여백 추가
                            // Navigation: padding 10px + height 40px + gap 10px = 약 60px
                            paddingTop: '10px',
                        }}>
                            <ChampionshipDetailContent
                                championshipData={selectedChampionshipData}
                            />
                        </Box>
                    )}

                    {/* 닫기 버튼 - 아래쪽 가운데 */}
                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        padding: '20px',
                        flexShrink: 0,
                        borderTop: `1px solid ${theme.palette.figma.white[10]}`,
                        position: 'relative',
                        zIndex: 2,
                    }}>
                        <Button onClick={handleCloseDetailDialog}>
                            {t('close')}
                        </Button>
                    </Box>
                </DialogContent>
            </Dialog>
        </Box>
    );
};
